// Database service for financial data operations
import { supabase } from '../lib/supabase';

// Helper function to check schema errors
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// ============================================================================
// FINANCIAL ACCOUNTS
// ============================================================================

export const financialAccountsService = {
  async getAll(userId) {
    try {
      const { data, error } = await supabase?.from('financial_accounts')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error fetching accounts:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll accounts:', error);
      throw error;
    }
  },

  async create(userId, accountData) {
    try {
      const { data, error } = await supabase?.from('financial_accounts')?.insert(toSnakeCase({ userId, ...accountData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating account:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create account:', error);
      throw error;
    }
  },

  async update(accountId, updates) {
    try {
      const { data, error } = await supabase?.from('financial_accounts')?.update(toSnakeCase(updates))?.eq('id', accountId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error updating account:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in update account:', error);
      throw error;
    }
  },

  async delete(accountId) {
    try {
      const { error } = await supabase?.from('financial_accounts')?.delete()?.eq('id', accountId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error deleting account:', error?.message);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Schema error in delete account:', error);
      throw error;
    }
  }
};

// ============================================================================
// TRANSACTIONS
// ============================================================================

export const transactionsService = {
  async getAll(userId, filters = {}) {
    try {
      let query = supabase?.from('transactions')?.select('*')?.eq('user_id', userId);
      
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }
      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      if (filters?.startDate) {
        query = query?.gte('transaction_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('transaction_date', filters?.endDate);
      }
      
      query = query?.order('transaction_date', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching transactions:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll transactions:', error);
      throw error;
    }
  },

  async create(userId, transactionData) {
    try {
      const { data, error } = await supabase?.from('transactions')?.insert(toSnakeCase({ userId, ...transactionData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating transaction:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create transaction:', error);
      throw error;
    }
  },

  async update(transactionId, updates) {
    try {
      const { data, error } = await supabase?.from('transactions')?.update(toSnakeCase(updates))?.eq('id', transactionId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error updating transaction:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in update transaction:', error);
      throw error;
    }
  },

  async delete(transactionId) {
    try {
      const { error } = await supabase?.from('transactions')?.delete()?.eq('id', transactionId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error deleting transaction:', error?.message);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Schema error in delete transaction:', error);
      throw error;
    }
  }
};

// ============================================================================
// BUDGETS
// ============================================================================

export const budgetsService = {
  async getAll(userId, month, year) {
    try {
      let query = supabase?.from('budgets')?.select('*')?.eq('user_id', userId);
      
      if (month) query = query?.eq('month', month);
      if (year) query = query?.eq('year', year);
      
      query = query?.order('category', { ascending: true });
      
      const { data, error } = await query;
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching budgets:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll budgets:', error);
      throw error;
    }
  },

  async create(userId, budgetData) {
    try {
      const { data, error } = await supabase?.from('budgets')?.insert(toSnakeCase({ userId, ...budgetData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating budget:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create budget:', error);
      throw error;
    }
  },

  async update(budgetId, updates) {
    try {
      const { data, error } = await supabase?.from('budgets')?.update(toSnakeCase(updates))?.eq('id', budgetId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error updating budget:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in update budget:', error);
      throw error;
    }
  },

  async delete(budgetId) {
    try {
      const { error } = await supabase?.from('budgets')?.delete()?.eq('id', budgetId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error deleting budget:', error?.message);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Schema error in delete budget:', error);
      throw error;
    }
  }
};

// ============================================================================
// GOALS
// ============================================================================

export const goalsService = {
  async getAll(userId) {
    try {
      const { data, error } = await supabase?.from('goals')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching goals:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll goals:', error);
      throw error;
    }
  },

  async create(userId, goalData) {
    try {
      const { data, error } = await supabase?.from('goals')?.insert(toSnakeCase({ userId, ...goalData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating goal:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create goal:', error);
      throw error;
    }
  },

  async update(goalId, updates) {
    try {
      const { data, error } = await supabase?.from('goals')?.update(toSnakeCase(updates))?.eq('id', goalId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error updating goal:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in update goal:', error);
      throw error;
    }
  },

  async delete(goalId) {
    try {
      const { error } = await supabase?.from('goals')?.delete()?.eq('id', goalId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error deleting goal:', error?.message);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Schema error in delete goal:', error);
      throw error;
    }
  }
};

// ============================================================================
// NET WORTH
// ============================================================================

export const netWorthService = {
  async getHistory(userId, limit = 12) {
    try {
      const { data, error } = await supabase?.from('net_worth')?.select('*')?.eq('user_id', userId)?.order('recorded_at', { ascending: false })?.limit(limit);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching net worth:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getHistory net worth:', error);
      throw error;
    }
  },

  async create(userId, netWorthData) {
    try {
      const { data, error } = await supabase?.from('net_worth')?.insert(toSnakeCase({ userId, ...netWorthData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating net worth record:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create net worth:', error);
      throw error;
    }
  }
};

// ============================================================================
// RECURRING SUBSCRIPTIONS
// ============================================================================

export const subscriptionsService = {
  async getAll(userId) {
    try {
      const { data, error } = await supabase?.from('recurring_subscriptions')?.select('*')?.eq('user_id', userId)?.order('next_billing_date', { ascending: true });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching subscriptions:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll subscriptions:', error);
      throw error;
    }
  },

  async create(userId, subscriptionData) {
    try {
      const { data, error } = await supabase?.from('recurring_subscriptions')?.insert(toSnakeCase({ userId, ...subscriptionData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating subscription:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in create subscription:', error);
      throw error;
    }
  },

  async update(subscriptionId, updates) {
    try {
      const { data, error } = await supabase?.from('recurring_subscriptions')?.update(toSnakeCase(updates))?.eq('id', subscriptionId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error updating subscription:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in update subscription:', error);
      throw error;
    }
  },

  async delete(subscriptionId) {
    try {
      const { error } = await supabase?.from('recurring_subscriptions')?.delete()?.eq('id', subscriptionId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error deleting subscription:', error?.message);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Schema error in delete subscription:', error);
      throw error;
    }
  }
};

// ============================================================================
// ENTERPRISE CLIENTS (Admin only)
// ============================================================================

export const enterpriseService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('enterprise_clients')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching enterprise clients:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAll enterprise:', error);
      throw error;
    }
  },

  async getEmployees(enterpriseId) {
    try {
      const { data, error } = await supabase?.from('enterprise_employees')?.select('*')?.eq('enterprise_id', enterpriseId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching employees:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getEmployees:', error);
      throw error;
    }
  }
};

// ============================================================================
// RETAIL PARTNERS
// ============================================================================

export const partnersService = {
  async get(userId) {
    try {
      const { data, error } = await supabase?.from('retail_partners')?.select('*')?.eq('user_id', userId)?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching partner:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in get partner:', error);
      throw error;
    }
  },

  async getCampaigns(partnerId) {
    try {
      const { data, error } = await supabase?.from('campaigns')?.select('*')?.eq('partner_id', partnerId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching campaigns:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getCampaigns:', error);
      throw error;
    }
  },

  async createCampaign(partnerId, campaignData) {
    try {
      const { data, error } = await supabase?.from('campaigns')?.insert(toSnakeCase({ partnerId, ...campaignData }))?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Error creating campaign:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Schema error in createCampaign:', error);
      throw error;
    }
  }
};

// ============================================================================
// SYSTEM ADMIN (Admin only)
// ============================================================================

export const adminService = {
  async getSystemHealth() {
    try {
      const { data, error } = await supabase?.from('system_health')?.select('*')?.order('recorded_at', { ascending: false })?.limit(10);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching system health:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getSystemHealth:', error);
      throw error;
    }
  },

  async getFraudAlerts() {
    try {
      const { data, error } = await supabase?.from('fraud_alerts')?.select('*')?.order('detected_at', { ascending: false })?.limit(50);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching fraud alerts:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getFraudAlerts:', error);
      throw error;
    }
  },

  async getAuditLogs(limit = 100) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching audit logs:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAuditLogs:', error);
      throw error;
    }
  },

  async getAllUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching users:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Schema error in getAllUsers:', error);
      throw error;
    }
  }
};